import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Search, Plus, Wrench, Eye, Shield, ShieldOff, Filter } from "lucide-react";

const statusConfig = {
  appareil_depose: { label: "Déposé", color: "bg-blue-100 text-blue-800" },
  en_attente_diagnostic: { label: "Attente diag.", color: "bg-yellow-100 text-yellow-800" },
  diagnostic_en_cours: { label: "Diagnostic", color: "bg-yellow-100 text-yellow-800" },
  en_attente_devis: { label: "Attente devis", color: "bg-purple-100 text-purple-800" },
  devis_envoye: { label: "Devis envoyé", color: "bg-purple-100 text-purple-800" },
  devis_accepte: { label: "Devis accepté", color: "bg-green-100 text-green-800" },
  devis_refuse: { label: "Devis refusé", color: "bg-red-100 text-red-800" },
  en_attente_pieces: { label: "Attente pièces", color: "bg-orange-100 text-orange-800" },
  reparation_en_cours: { label: "En réparation", color: "bg-orange-100 text-orange-800" },
  test_final: { label: "Test final", color: "bg-cyan-100 text-cyan-800" },
  pret_a_restituer: { label: "Prêt", color: "bg-green-100 text-green-800" },
  restitue: { label: "Restitué", color: "bg-gray-100 text-gray-800" },
  cloture: { label: "Clôturé", color: "bg-gray-100 text-gray-800" },
  refus_garantie: { label: "Garantie refusée", color: "bg-red-100 text-red-800" },
  irreparable: { label: "Irréparable", color: "bg-red-100 text-red-800" },
  sans_suite: { label: "Sans suite", color: "bg-gray-100 text-gray-800" }
};

const priorityConfig = {
  basse: { label: "Basse", color: "bg-gray-100 text-gray-700" },
  normale: { label: "Normale", color: "bg-blue-100 text-blue-700" },
  haute: { label: "Haute", color: "bg-orange-100 text-orange-700" },
  urgente: { label: "Urgente", color: "bg-red-100 text-red-700" }
};

const SAVCasesPage = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [devices, setDevices] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [formData, setFormData] = useState({
    client_id: "",
    device_id: "",
    type_prise_en_charge: "hors_garantie",
    priorite: "normale",
    technicien_id: "",
    date_estimee_fin: "",
    commentaires: "",
    date_achat: "",
    facture_achat: "",
    duree_garantie: "",
    fournisseur: "",
    type_garantie: ""
  });

  const fetchCases = async () => {
    setLoading(true);
    try {
      const params = { search, limit: 100 };
      if (statusFilter) params.statut = statusFilter;
      if (typeFilter) params.type_prise_en_charge = typeFilter;
      const response = await API.get("/sav-cases", { params });
      setCases(response.data.cases);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await API.get("/clients", { params: { limit: 200 } });
      setClients(response.data.clients);
    } catch (error) {
      console.error("Error fetching clients");
    }
  };

  const fetchClientDevices = async (clientId) => {
    try {
      const response = await API.get("/devices", { params: { client_id: clientId, limit: 50 } });
      setDevices(response.data.devices);
    } catch (error) {
      console.error("Error fetching devices");
    }
  };

  useEffect(() => {
    fetchClients();
    const fetchTechs = async () => {
      try {
        const res = await API.get("/users/technicians");
        setTechnicians(res.data);
      } catch (e) { /* ignore */ }
    };
    fetchTechs();
  }, []);

  useEffect(() => {
    const timer = setTimeout(fetchCases, 300);
    return () => clearTimeout(timer);
  }, [search, statusFilter, typeFilter]);

  useEffect(() => {
    if (selectedClientId) {
      fetchClientDevices(selectedClientId);
      setFormData({ ...formData, client_id: selectedClientId, device_id: "" });
    }
  }, [selectedClientId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id) {
      toast.error("Veuillez sélectionner un client");
      return;
    }
    if (!formData.device_id) {
      toast.error("Veuillez sélectionner ou ajouter un appareil");
      return;
    }
    try {
      const data = { ...formData };
      // Clean empty strings for optional fields
      if (data.duree_garantie) {
        data.duree_garantie = parseInt(data.duree_garantie);
      } else {
        delete data.duree_garantie;
      }
      if (!data.technicien_id) delete data.technicien_id;
      if (!data.date_estimee_fin) delete data.date_estimee_fin;
      if (!data.commentaires) delete data.commentaires;
      if (!data.date_achat) delete data.date_achat;
      if (!data.facture_achat) delete data.facture_achat;
      if (!data.fournisseur) delete data.fournisseur;
      if (!data.type_garantie) delete data.type_garantie;
      
      const response = await API.post("/sav-cases", data);
      toast.success("Dossier SAV créé");
      // Navigate to the new case instead of closing dialog (avoids Radix unmount crash on Safari)
      const newCaseId = response.data?.id;
      if (newCaseId) {
        navigate(`/sav/${newCaseId}`);
      } else {
        navigate("/sav");
        window.location.reload();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg).join(", "));
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Erreur lors de la création");
      }
    }
  };

  const resetForm = () => {
    setSelectedClientId("");
    setDevices([]);
    setFormData({
      client_id: "",
      device_id: "",
      type_prise_en_charge: "hors_garantie",
      priorite: "normale",
      technicien_id: "",
      date_estimee_fin: "",
      commentaires: "",
      date_achat: "",
      facture_achat: "",
      duree_garantie: "",
      fournisseur: "",
      type_garantie: ""
    });
  };

  return (
    <div className="space-y-6" data-testid="sav-cases-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dossiers SAV</h1>
          <p className="text-muted-foreground">{total} dossiers</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-sav-case-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau dossier
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>Nouveau dossier SAV</DialogTitle>
                <DialogDescription>Créez un nouveau dossier de service après-vente</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                {/* Client Selection */}
                <div className="space-y-2">
                  <Label>Client *</Label>
                  <select
                    value={selectedClientId}
                    onChange={(e) => setSelectedClientId(e.target.value)}
                    required
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid="sav-client-select"
                  >
                    <option value="">Sélectionner un client</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.prenom} {client.nom} - {client.telephone}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Device Selection */}
                <div className="space-y-2">
                  <Label>Appareil *</Label>
                  <select
                    value={formData.device_id}
                    onChange={(e) => setFormData({ ...formData, device_id: e.target.value })}
                    disabled={!selectedClientId}
                    required
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                    data-testid="sav-device-select"
                  >
                    <option value="">{selectedClientId ? "Sélectionner un appareil" : "Sélectionnez d'abord un client"}</option>
                    {devices.map((device) => (
                      <option key={device.id} value={device.id}>
                        {device.marque} {device.modele} - {device.panne_declaree?.substring(0, 30)}...
                      </option>
                    ))}
                  </select>
                  {selectedClientId && devices.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      Aucun appareil enregistré pour ce client. <Button variant="link" className="p-0 h-auto" onClick={() => navigate("/devices")}>Ajouter un appareil</Button>
                    </p>
                  )}
                </div>

                {/* Type de prise en charge */}
                <div className="space-y-2">
                  <Label>Type de prise en charge *</Label>
                  <Tabs
                    value={formData.type_prise_en_charge}
                    onValueChange={(value) => setFormData({ ...formData, type_prise_en_charge: value })}
                  >
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="garantie" className="gap-2" data-testid="type-garantie">
                        <Shield className="h-4 w-4" />
                        Sous garantie
                      </TabsTrigger>
                      <TabsTrigger value="hors_garantie" className="gap-2" data-testid="type-hors-garantie">
                        <ShieldOff className="h-4 w-4" />
                        Hors garantie
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>

                {/* Warranty fields */}
                {formData.type_prise_en_charge === "garantie" && (
                  <div className="space-y-4 p-4 rounded-lg bg-secondary/50">
                    <p className="text-sm font-medium">Informations de garantie</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Date d'achat</Label>
                        <Input
                          type="date"
                          value={formData.date_achat}
                          onChange={(e) => setFormData({ ...formData, date_achat: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Durée garantie (mois)</Label>
                        <Input
                          type="number"
                          value={formData.duree_garantie}
                          onChange={(e) => setFormData({ ...formData, duree_garantie: e.target.value })}
                          placeholder="Ex: 24"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Fournisseur / Constructeur</Label>
                      <Input
                        value={formData.fournisseur}
                        onChange={(e) => setFormData({ ...formData, fournisseur: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Type de garantie</Label>
                      <select
                        value={formData.type_garantie}
                        onChange={(e) => setFormData({ ...formData, type_garantie: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <option value="">Sélectionner</option>
                        <option value="magasin">Garantie magasin</option>
                        <option value="constructeur">Garantie constructeur</option>
                        <option value="extension">Extension de garantie</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Priority */}
                <div className="space-y-2">
                  <Label>Priorité</Label>
                  <select
                    value={formData.priorite}
                    onChange={(e) => setFormData({ ...formData, priorite: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid="sav-priority-select"
                  >
                    <option value="basse">Basse</option>
                    <option value="normale">Normale</option>
                    <option value="haute">Haute</option>
                    <option value="urgente">Urgente</option>
                  </select>
                </div>

                {/* Technician Assignment */}
                {technicians.length > 0 && (
                  <div className="space-y-2">
                    <Label>Technicien assigné</Label>
                    <select
                      value={formData.technicien_id || ""}
                      onChange={(e) => setFormData({ ...formData, technicien_id: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid="sav-technician-select"
                    >
                      <option value="">Non assigné</option>
                      {technicians.map((tech) => (
                        <option key={tech.id} value={tech.id}>
                          {tech.prenom} {tech.nom}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Estimated end date */}
                <div className="space-y-2">
                  <Label>Date estimée de fin</Label>
                  <Input
                    type="date"
                    value={formData.date_estimee_fin}
                    onChange={(e) => setFormData({ ...formData, date_estimee_fin: e.target.value })}
                  />
                </div>

                {/* Comments */}
                <div className="space-y-2">
                  <Label>Commentaires</Label>
                  <Textarea
                    value={formData.commentaires}
                    onChange={(e) => setFormData({ ...formData, commentaires: e.target.value })}
                    placeholder="Notes internes sur le dossier"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" data-testid="sav-submit-btn">Créer le dossier</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par numéro de dossier..."
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="sav-search-input"
              />
            </div>
            <select
              value={statusFilter || "all"}
              onChange={(e) => setStatusFilter(e.target.value === "all" ? "" : e.target.value)}
              className="flex h-10 w-full sm:w-48 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="all">Tous les statuts</option>
              {Object.entries(statusConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
            <select
              value={typeFilter || "all"}
              onChange={(e) => setTypeFilter(e.target.value === "all" ? "" : e.target.value)}
              className="flex h-10 w-full sm:w-48 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="all">Tous les types</option>
              <option value="garantie">Sous garantie</option>
              <option value="hors_garantie">Hors garantie</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : cases.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <Wrench className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="font-medium">Aucun dossier SAV trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Numéro</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead className="hidden md:table-cell">Appareil</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="hidden sm:table-cell">Type</TableHead>
                    <TableHead className="hidden lg:table-cell">Priorité</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cases.map((sav) => (
                    <TableRow key={sav.id} data-testid={`sav-row-${sav.id}`}>
                      <TableCell className="font-mono font-medium">{sav.numero}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{sav.client?.prenom} {sav.client?.nom}</p>
                          <p className="text-sm text-muted-foreground">{sav.client?.telephone}</p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {sav.device?.marque} {sav.device?.modele}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig[sav.statut]?.color || "bg-gray-100"}>
                          {statusConfig[sav.statut]?.label || sav.statut}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <Badge variant={sav.type_prise_en_charge === "garantie" ? "default" : "secondary"}>
                          {sav.type_prise_en_charge === "garantie" ? (
                            <><Shield className="h-3 w-3 mr-1" /> Garantie</>
                          ) : (
                            <><ShieldOff className="h-3 w-3 mr-1" /> Hors garantie</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <Badge className={priorityConfig[sav.priorite]?.color || ""}>
                          {priorityConfig[sav.priorite]?.label || sav.priorite}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/sav/${sav.id}`)}
                          data-testid={`view-sav-${sav.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SAVCasesPage;
